import React, { useState, useRef, useCallback, useEffect } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Slider } from '../../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, BarChart3, Play, Pause, RotateCcw, Shuffle, Zap, Info, Star, Trophy, ArrowRightLeft
} from 'lucide-react';

const ALGORITHMS = {
  bubble: { name: 'Bubble Sort', color: '#60a5fa', complexity: 'O(n²)' },
  selection: { name: 'Selection Sort', color: '#f87171', complexity: 'O(n²)' },
  insertion: { name: 'Insertion Sort', color: '#4ade80', complexity: 'O(n²)' },
  merge: { name: 'Merge Sort', color: '#a78bfa', complexity: 'O(n log n)' },
};

function generateArray(size) {
  return Array.from({ length: size }, () => Math.floor(Math.random() * 90) + 10);
}

function BarVisualization({ array, comparing, swapping, sorted, color, label }) {
  const maxVal = Math.max(...array, 1);

  return (
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-3 h-3 rounded-full" style={{ background: color }} />
        <span className="text-sm font-semibold">{label}</span>
      </div>
      <div className="flex items-end gap-[2px] h-[250px] p-3 rounded-xl bg-card border border-border/50">
        {array.map((val, idx) => {
          const isComparing = comparing.includes(idx);
          const isSwapping = swapping.includes(idx);
          const isSorted = sorted.includes(idx);
          
          let barColor = color + '60';
          if (isSorted) barColor = '#22c55e';
          else if (isSwapping) barColor = '#facc15';
          else if (isComparing) barColor = '#f87171';

          return (
            <motion.div
              key={idx}
              className="flex-1 rounded-t-sm relative"
              style={{ background: barColor, height: `${(val / maxVal) * 100}%` }}
              layout
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            >
              {array.length <= 20 && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] text-muted-foreground font-mono">
                  {val}
                </span>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default function SortingLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('sorting');
  const navigate = useNavigate();
  const [arraySize, setArraySize] = useState(20);
  const [speed, setSpeed] = useState(50);
  const [algorithm1, setAlgorithm1] = useState('bubble');
  const [algorithm2, setAlgorithm2] = useState('selection');
  const [isRaceMode, setIsRaceMode] = useState(false);

  const [array1, setArray1] = useState(() => generateArray(20));
  const [array2, setArray2] = useState(() => [...array1]);
  const [comparing1, setComparing1] = useState([]);
  const [comparing2, setComparing2] = useState([]);
  const [swapping1, setSwapping1] = useState([]);
  const [swapping2, setSwapping2] = useState([]);
  const [sorted1, setSorted1] = useState([]);
  const [sorted2, setSorted2] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [stats1, setStats1] = useState({ comparisons: 0, swaps: 0 });
  const [stats2, setStats2] = useState({ comparisons: 0, swaps: 0 });
  const [winner, setWinner] = useState(null);

  const cancelRef = useRef(false);
  const runningRef = useRef(false);

  const delay = useCallback(() => new Promise(r => setTimeout(r, Math.max(5, 200 - speed * 2))), [speed]);

  const resetArrays = () => {
    cancelRef.current = true;
    runningRef.current = false;
    const newArr = generateArray(arraySize);
    setArray1([...newArr]);
    setArray2([...newArr]);
    setComparing1([]); setComparing2([]);
    setSwapping1([]); setSwapping2([]);
    setSorted1([]); setSorted2([]);
    setStats1({ comparisons: 0, swaps: 0 });
    setStats2({ comparisons: 0, swaps: 0 });
    setIsRunning(false);
    setWinner(null);
    setTimeout(() => { cancelRef.current = false; }, 50);
  };

  const shuffleArrays = () => {
    if (isRunning) return;
    resetArrays();
  };

  // Sort algorithms
  const bubbleSort = async (arr, setArr, setComp, setSwap, setSrt, setStatsFn, label) => {
    const a = [...arr];
    let comps = 0, swaps = 0;
    for (let i = 0; i < a.length && !cancelRef.current; i++) {
      for (let j = 0; j < a.length - i - 1 && !cancelRef.current; j++) {
        setComp([j, j + 1]);
        comps++;
        setStatsFn({ comparisons: comps, swaps });
        await delay();
        if (a[j] > a[j + 1]) {
          [a[j], a[j + 1]] = [a[j + 1], a[j]];
          swaps++;
          setSwap([j, j + 1]);
          setArr([...a]);
          setStatsFn({ comparisons: comps, swaps });
          await delay();
          setSwap([]);
        }
      }
      setSrt(prev => [...prev, a.length - 1 - i]);
    }
    setComp([]);
    setSrt(a.map((_, i) => i));
    return label;
  };

  const selectionSort = async (arr, setArr, setComp, setSwap, setSrt, setStatsFn, label) => {
    const a = [...arr];
    let comps = 0, swaps = 0;
    for (let i = 0; i < a.length && !cancelRef.current; i++) {
      let minIdx = i;
      for (let j = i + 1; j < a.length && !cancelRef.current; j++) {
        setComp([minIdx, j]);
        comps++;
        setStatsFn({ comparisons: comps, swaps });
        await delay();
        if (a[j] < a[minIdx]) minIdx = j;
      }
      if (minIdx !== i) {
        [a[i], a[minIdx]] = [a[minIdx], a[i]];
        swaps++;
        setSwap([i, minIdx]);
        setArr([...a]);
        setStatsFn({ comparisons: comps, swaps });
        await delay();
        setSwap([]);
      }
      setSrt(prev => [...prev, i]);
    }
    setComp([]);
    setSrt(a.map((_, i) => i));
    return label;
  };

  const insertionSort = async (arr, setArr, setComp, setSwap, setSrt, setStatsFn, label) => {
    const a = [...arr];
    let comps = 0, swaps = 0;
    setSrt([0]);
    for (let i = 1; i < a.length && !cancelRef.current; i++) {
      let j = i;
      while (j > 0 && !cancelRef.current) {
        setComp([j - 1, j]);
        comps++;
        setStatsFn({ comparisons: comps, swaps });
        await delay();
        if (a[j - 1] > a[j]) {
          [a[j - 1], a[j]] = [a[j], a[j - 1]];
          swaps++;
          setSwap([j - 1, j]);
          setArr([...a]);
          setStatsFn({ comparisons: comps, swaps });
          await delay();
          setSwap([]);
          j--;
        } else break;
      }
      setSrt(prev => [...new Set([...prev, i])]);
    }
    setComp([]);
    setSrt(a.map((_, i) => i));
    return label;
  };

  const mergeSort = async (arr, setArr, setComp, setSwap, setSrt, setStatsFn, label) => {
    const a = [...arr];
    let comps = 0, swaps = 0;

    const merge = async (left, right, start) => {
      const result = [];
      let i = 0, j = 0;
      while (i < left.length && j < right.length && !cancelRef.current) {
        setComp([start + i, start + left.length + j]);
        comps++;
        setStatsFn({ comparisons: comps, swaps });
        await delay();
        if (left[i] <= right[j]) {
          result.push(left[i++]);
        } else {
          result.push(right[j++]);
          swaps++;
          setStatsFn({ comparisons: comps, swaps });
        }
      }
      return [...result, ...left.slice(i), ...right.slice(j)];
    };

    const sort = async (subArr, start) => {
      if (subArr.length <= 1 || cancelRef.current) return subArr;
      const mid = Math.floor(subArr.length / 2);
      const left = await sort(subArr.slice(0, mid), start);
      const right = await sort(subArr.slice(mid), start + mid);
      const merged = await merge(left, right, start);
      // Update main array
      for (let k = 0; k < merged.length; k++) {
        a[start + k] = merged[k];
      }
      setArr([...a]);
      setSwap(Array.from({ length: merged.length }, (_, k) => start + k));
      await delay();
      setSwap([]);
      return merged;
    };

    await sort(a, 0);
    setComp([]);
    setSrt(a.map((_, i) => i));
    return label;
  };

  const getAlgorithm = (name) => {
    switch (name) {
      case 'bubble': return bubbleSort;
      case 'selection': return selectionSort;
      case 'insertion': return insertionSort;
      case 'merge': return mergeSort;
      default: return bubbleSort;
    }
  };

  const runSort = async () => {
    if (runningRef.current) return;
    cancelRef.current = false;
    runningRef.current = true;
    setIsRunning(true);
    setWinner(null);
    setSorted1([]); setSorted2([]);
    setStats1({ comparisons: 0, swaps: 0 });
    setStats2({ comparisons: 0, swaps: 0 });

    const algo1 = getAlgorithm(algorithm1);

    if (isRaceMode) {
      const algo2 = getAlgorithm(algorithm2);
      const result = await Promise.race([
        algo1(array1, setArray1, setComparing1, setSwapping1, setSorted1, setStats1, algorithm1),
        algo2(array2, setArray2, setComparing2, setSwapping2, setSorted2, setStats2, algorithm2),
      ]);
      if (!cancelRef.current) setWinner(result);
      // Let the other one finish
      await Promise.all([
        algo1(array1, setArray1, setComparing1, setSwapping1, setSorted1, setStats1, algorithm1).catch(() => {}),
        algo2(array2, setArray2, setComparing2, setSwapping2, setSorted2, setStats2, algorithm2).catch(() => {}),
      ]).catch(() => {});
    } else {
      await algo1(array1, setArray1, setComparing1, setSwapping1, setSorted1, setStats1, algorithm1);
    }

    setIsRunning(false);
    runningRef.current = false;
  };

  useEffect(() => {
    return () => { cancelRef.current = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="sorting-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="Computer Science lab about sorting algorithms. Watch Bubble Sort, Selection Sort, and Merge Sort race to sort arrays step by step."
        cacheKey="lab-intro-sorting"
        onClose={dismiss} dismissed={dismissed} onResummon={resummon} />
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              <span className="font-semibold">Sorting Algorithm Arena</span>
            </div>
          </div>
          <Button
            variant={isRaceMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => { if (!isRunning) { setIsRaceMode(!isRaceMode); resetArrays(); } }}
            data-testid="race-mode-toggle"
          >
            <Trophy className="w-4 h-4 mr-2" />
            Race Mode {isRaceMode ? 'ON' : 'OFF'}
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-4 mb-6 p-4 rounded-xl bg-card border border-border/50">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Algorithm:</span>
            <Select value={algorithm1} onValueChange={v => { if (!isRunning) setAlgorithm1(v); }}>
              <SelectTrigger className="w-40" data-testid="algo1-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ALGORITHMS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isRaceMode && (
            <>
              <span className="text-muted-foreground">vs</span>
              <Select value={algorithm2} onValueChange={v => { if (!isRunning) setAlgorithm2(v); }}>
                <SelectTrigger className="w-40" data-testid="algo2-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ALGORITHMS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-muted-foreground">Size: {arraySize}</span>
            <div className="w-24">
              <Slider
                value={[arraySize]}
                onValueChange={v => { if (!isRunning) { setArraySize(v[0]); } }}
                min={5} max={50} step={1}
                data-testid="size-slider"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Speed:</span>
            <div className="w-24">
              <Slider
                value={[speed]}
                onValueChange={v => setSpeed(v[0])}
                min={1} max={100} step={1}
                data-testid="speed-slider"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={runSort} disabled={isRunning} data-testid="start-sort-btn">
              <Play className="w-4 h-4 mr-1" /> Start
            </Button>
            <Button variant="outline" onClick={shuffleArrays} disabled={isRunning} data-testid="shuffle-btn">
              <Shuffle className="w-4 h-4 mr-1" /> Shuffle
            </Button>
            <Button variant="outline" onClick={resetArrays} data-testid="reset-sort-btn">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Winner banner */}
        <AnimatePresence>
          {winner && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center gap-3"
            >
              <Trophy className="w-5 h-5 text-yellow-400" />
              <span className="font-semibold">{ALGORITHMS[winner].name} wins the race!</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Visualizations */}
        <div className={`flex gap-6 ${isRaceMode ? '' : 'max-w-4xl mx-auto'}`}>
          <BarVisualization
            array={array1}
            comparing={comparing1}
            swapping={swapping1}
            sorted={sorted1}
            color={ALGORITHMS[algorithm1].color}
            label={`${ALGORITHMS[algorithm1].name} (${ALGORITHMS[algorithm1].complexity})`}
          />
          {isRaceMode && (
            <BarVisualization
              array={array2}
              comparing={comparing2}
              swapping={swapping2}
              sorted={sorted2}
              color={ALGORITHMS[algorithm2].color}
              label={`${ALGORITHMS[algorithm2].name} (${ALGORITHMS[algorithm2].complexity})`}
            />
          )}
        </div>

        {/* Stats */}
        <div className={`grid gap-4 mt-6 ${isRaceMode ? 'grid-cols-2' : 'grid-cols-1 max-w-4xl mx-auto'}`}>
          <div className="p-4 rounded-xl bg-card border border-border/50">
            <h4 className="text-sm font-semibold mb-2" style={{ color: ALGORITHMS[algorithm1].color }}>
              {ALGORITHMS[algorithm1].name} Stats
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Comparisons</span>
                <div className="font-mono text-lg">{stats1.comparisons}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Swaps</span>
                <div className="font-mono text-lg">{stats1.swaps}</div>
              </div>
            </div>
          </div>
          {isRaceMode && (
            <div className="p-4 rounded-xl bg-card border border-border/50">
              <h4 className="text-sm font-semibold mb-2" style={{ color: ALGORITHMS[algorithm2].color }}>
                {ALGORITHMS[algorithm2].name} Stats
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Comparisons</span>
                  <div className="font-mono text-lg">{stats2.comparisons}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Swaps</span>
                  <div className="font-mono text-lg">{stats2.swaps}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="mt-6 p-4 rounded-xl bg-muted/30 border border-border/30 max-w-4xl mx-auto">
          <h4 className="text-xs font-semibold flex items-center gap-1 mb-2 text-muted-foreground">
            <Info className="w-3 h-3" /> About Sorting Algorithms
          </h4>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Sorting algorithms arrange elements in order. <strong>Bubble Sort</strong> repeatedly swaps adjacent elements.
            <strong> Selection Sort</strong> finds the minimum and places it at the front.
            <strong> Insertion Sort</strong> builds the sorted array one element at a time.
            <strong> Merge Sort</strong> divides the array and merges sorted halves — it's the fastest at O(n log n)!
          </p>
        </div>
      </div>
    </div>
  );
}
